/**
 * Similar to triple-equals, but compares NaNs as equal and positive and
 * negative 0 to be equal.  This is useful for map keys and diffing.
 *
 * This function is copied from MDN:
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Equality_comparisons_and_sameness#same-value-zero_equality
 * @param {string | number | object | null | undefined} x
 * @param {string | number | object | null | undefined} y
 * @returns {boolean}
 */
export function sameValueZero(x: unknown, y: unknown): boolean {
  if (typeof x === 'number' && typeof y === 'number') {
    // x and y are equal (may be -0 and 0) or they are both NaN
    // eslint-disable-next-line no-self-compare
    return x === y || (x !== x && y !== y);
  }
  return x === y;
}

/**
 * Shallow clone an object, preserving its class.
 * @template T
 * @param {T} obj
 * @return {T}
 */
export function shallowClone<T>(obj: T): T {
  // null is type object.
  if (obj === null || obj === undefined) return obj;
  if (typeof obj !== 'object') return obj;

  if (Array.isArray(obj)) return obj.slice() as T;
  return Object.assign(Object.create(Object.getPrototypeOf(obj)), obj) as T;
}

/**
 * Sort an array of strings, case-insensitively to present in UIs.
 * @param {Array<string>} arr
 * @return {Array<string>}
 */
export function sortCaseInsensitive(arr: string[]): string[] {
  const collator = new Intl.Collator(undefined, {sensitivity: 'base'});
  const copy = arr.slice();
  // JS sort mutates in-place.
  copy.sort((a: string, b: string) => {
    return collator.compare(a, b);
  });
  return copy;
}

/**
 * Sort an array of objects by the result of a mapping function,
 * case-insensitively to present in UIs.
 *
 * Based on: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort#sorting_with_map
 * @template T
 * @param {Array<T>} arr
 * @param {(T) => string} fn
 * @return {Array<T>}
 */
export function sortCaseInsensitiveBy<T>(arr: T[], fn: (v: T) => string): T[] {
  const mapped = arr.map((value: T, i: number) => {
    return {index: i, key: fn(value)};
  });
  const collator = new Intl.Collator(undefined, {sensitivity: 'base'});
  // JS sort mutates in-place.
  mapped.sort((a: {index: number, key: string}, b: {index: number, key: string}) => {
    return collator.compare(a.key, b.key);
  });
  return mapped.map(({index}: {index: number}) => arr[index]);
}

/**
 * Returns true if the value is null or undefined.
 * @param {string | number | object | null | undefined} value
 * @returns {value is null | undefined}
 */
export function isNone(value: unknown): value is null | undefined {
  return value === null || value === undefined;
}

/**
 * Clamps a number within the specified range.
 * @param {number} x - The input number to be clamped.
 * @param {number} min - The minimum value of the range.
 * @param {number} max - The maximum value of the range.
 * @returns {number} The clamped value within the specified range.
 */
export function clamp(x: number, min: number, max: number): number {
  if (Number.isNaN(x)) return x;
  if (x < min) return min;
  if (x > max) return max;
  return x;
}

/**
 * Clamps a number within the specified range.
 * @param {number} x - The input number to be clamped.
 * @param {number} min - The minimum value of the range.
 * @returns {number} The clamped value within the specified range.
 */
export function clampMin(x: number, min: number): number {
  if (Number.isNaN(x)) return x;
  if (x < min) return min;
  return x;
}
